<template>
  <div>
    <tool-bar :title="toolbarTitle"></tool-bar>
    <loading :loading="loading"></loading>

    <error-alert-item
        :error-alert-event="errorAlertEvent"
        :network-error="networkError"
        @resetErrorAlertEvent="errorAlertEvent=false"
    ></error-alert-item>

    <activity-redirect-item
        :loan-tasks="tasks"
        :current-loan-task="currentTaskName"
        :is-loan-task="true"
        :redirect-activity="redirectActivity"
    ></activity-redirect-item>

    <stage-comment-item></stage-comment-item>

    <refresh-item
        :show="showRefreshItem"
        @onRefresh="fetchInitialData"
    ></refresh-item>

    <bank-details-input-item
        class="margin-top-20"
        ref="bankDetailsInputs"
        :show="showBankDetailsInput"
        :bank-details-heading="bankDetailsHeading"
        :bank-details-description="bankDetailsDescription"
        @onBankDetailsAdded="onBankDetailsAdded"
    ></bank-details-input-item>


    <bank-statement-manual-upload-item
        ref="bankStatementUploadItem"
        :show="showBankStatementUploadItem"
        :upload-only-front-page="true"
        :allow-only-p-d-f="false"
        :instruction="bankStatementUploadInstruction"
        @onBankStatementsUploaded="completeCurrentTaskAndGetNextTask"
    ></bank-statement-manual-upload-item>
  </div>
</template>

<script>

import ToolBar from "../../layout/ToolBar";
import Loading from "../../layout/Loading";
import ActivityRedirectItem from "../../layout/ActivityRedirectItem";
import StageCommentItem from "../../layout/StageCommentItem";
import ErrorAlertItem from "../../layout/ErrorAlertItem";
import EzUtils from "../../../utils/EzUtils";
import {DataTables} from "@/utils/DataTables";
import {RolesConstants} from "@/utils/RolesConstants";
import {Activities} from "@/utils/ActivityNavigator";
import LoanTasksActivityNavigator from "../../../utils/LoanTasksActivityNavigator";
import LoanScopeDataHolder from "../../../data/model/LoanScopeDataHolder";
import PrefManager from "../../../data/model/PrefManager";
import DataSource from "../../../data/DataSource";
import {Constants} from "../../Constants";
import RefreshItem from "../../layout/RefreshItem";
import BankDetailsInputItem from "@/components/layout/BankDetailsInputItem";
import BankStatementManualUploadItem from "@/components/layout/BankStatementManualUploadItem";

export default {
  name: "BankDetailsActivity",
  components: {
    ActivityRedirectItem,
    ErrorAlertItem,
    ToolBar,
    Loading,
    StageCommentItem,
    RefreshItem,
    BankDetailsInputItem,
    BankStatementManualUploadItem
  },

  data: () => ({
    toolbarTitle: "Bank Details",
    loading: false,
    errorAlertEvent: false,
    networkError: {},
    showRefreshItem: false,
    showBankDetailsInput: false,
    showBankStatementUploadItem: false,
  }),

  methods: {
    showError(error) {
      this.loading = false;
      this.networkError = error;
      this.errorAlertEvent = true;
    },

    onBankDetailsAdded() {
      this.loading = true;
      DataSource.isFrontPageRequired(LoanScopeDataHolder.getLoanId())
      .then(frontPageRequiredRes => {
        this.loading = false;
        if (frontPageRequiredRes.errorOccurred) {
          this.showError(frontPageRequiredRes.data);
        } else {
          if (frontPageRequiredRes.data.isFrontPageRequired) {
            this.showBankDetailsInput = false;
            this.showBankStatementUploadItem = true;
            this.$refs.bankStatementUploadItem.fetchInitialData();
          } else {
            this.completeCurrentTaskAndGetNextTask();
          }
        }
      })
    },

    completeCurrentTaskAndGetNextTask() {
      this.loading = true;
      LoanTasksActivityNavigator.getNextTask(this.tasks)
      .then(res => {
        this.loading = false;
        if (res.errorOccurred) {
          this.showError(res.data);
        }
      });
    },

    fetchInitialData() {
      this.showRefreshItem = false;
      this.loading = true;
      DataSource.getLoanStageConfigData(LoanScopeDataHolder.getLoanId(), this.currentTaskName)
      .then(stageConfigDataRes => {
        if (stageConfigDataRes.errorOccurred) {
          this.showError(stageConfigDataRes.data);
          this.showRefreshItem = true;
        } else {
          const accountTypes = stageConfigDataRes.data && stageConfigDataRes.data.properties
              ? stageConfigDataRes.data.properties[Constants.BANK_ACCOUNT_TYPE_OPTIONS]
              : [];

          DataSource.getDataTableInfo(DataTables.BANK_DETAILS, LoanScopeDataHolder.getClientId())
          .then(bankDetailsDataRes => {
            this.loading = false;
            if (bankDetailsDataRes.errorOccurred) {
              this.showError(bankDetailsDataRes.data);
              this.showRefreshItem = true;
            } else {
              this.showBankDetailsInput = true;
              const bankDetails = EzUtils.isNotEmpty(bankDetailsDataRes.data)
                  ? bankDetailsDataRes.data[bankDetailsDataRes.data.length - 1] : {};

              this.$refs.bankDetailsInputs.showBankDetailsFields(accountTypes, bankDetails);
            }
          })
        }
      })
    }
  },

  mounted() {
    this.fetchInitialData();
  },

  computed: {
    isAgent() {
      return PrefManager.doesUserHasRole(RolesConstants.AGENT);
    },

    tasks() {
      return [Activities.BANK_DETAILS];
    },

    currentTaskName() {
      return LoanScopeDataHolder.getCurrentLoanTask()
          ? LoanScopeDataHolder.getCurrentLoanTask().name
          : "";
    },

    redirectActivity() {
      return Activities.LOAN_DETAILS;
    },

    bankDetailsHeading() {
      const bankAccountOwner = this.isAgent ? "customer's" : "your";
      return LoanScopeDataHolder.isOnboardingLoan()
          ? "Please enter bank account details."
          : `Please enter the details of ${bankAccountOwner} Bank Account.`;
    },

    bankDetailsDescription() {
      const bankAccountOwner = this.isAgent ? "customer's" : "your";
      return LoanScopeDataHolder.isOnboardingLoan()
          ? "(This will be the account where money will be disbursed to.)"
          : `(This will be used for ${bankAccountOwner} EMI Payments.)`;
    },

    bankStatementUploadInstruction() {
      return `<div style="text-align: center"><span style="font-size: 16px; color: black; font-weight: bold;">Please upload Bank Statement PDF or Front Page image</span></div>`;
    }
  }
}
</script>

<style scoped src="../../../assets/style/base.css"></style>

